import type {
  GraphInterface,
  TraversalViewModel,
  TraversalViewSettings,
  TraverseOptions,
} from '@ardoq/graph';
import { LoadedScenarioData } from 'loadedScenarioData$';
import { ReferenceTypeOptionCache } from 'modelInterface/graph/ReferenceTypeOptionCache';
import { GlobalReferenceTypeId, GraphModelShape } from '@ardoq/data-model';
import {
  ArdoqId,
  GroupType,
  GroupingRule,
  ReferenceDirection,
} from '@ardoq/api-types';
import type { ScenarioRelatedComponentIds } from 'streams/components/components$';

const referenceTypeOptionCache = new ReferenceTypeOptionCache();

const buildReferenceTypeMap = (graphModel: GraphModelShape) =>
  new Map(
    graphModel.referenceTypes.map(({ name, typeIds }) => [name, typeIds])
  );

const getReferenceTypeIdsFromName = (
  referenceTypeNames: string[],
  referenceTypes: Map<string, GlobalReferenceTypeId[]>
) =>
  new Set(
    referenceTypeNames.flatMap(referenceTypeName => {
      const referenceTypeIds = referenceTypes.get(referenceTypeName);
      return referenceTypeIds || [referenceTypeName];
    })
  );

interface GetTraverseOptionsArgs {
  viewSettings: TraversalViewSettings;
  referenceTypeMap: Map<string, string[]>;
  excludedComponentIds: Set<ArdoqId>;
}
const getTraverseOptions = ({
  viewSettings: {
    depthOfRelationship,
    incomingDegreesOfRelationship,
    outgoingDegreesOfRelationship,
    includeOutgoingReferenceTypeNames,
    includeIncomingReferenceTypeNames,
  },
  referenceTypeMap,
  excludedComponentIds,
}: GetTraverseOptionsArgs): TraverseOptions => ({
  maxDegreesIncoming: incomingDegreesOfRelationship,
  maxDegreesOutgoing: outgoingDegreesOfRelationship,
  maxDegrees: depthOfRelationship,
  isParentRelationAsReference: true,
  outgoingReferenceTypes: getReferenceTypeIdsFromName(
    includeOutgoingReferenceTypeNames,
    referenceTypeMap
  ),
  incomingReferenceTypes: getReferenceTypeIdsFromName(
    includeIncomingReferenceTypeNames,
    referenceTypeMap
  ),
  excludedComponentIds,
});
interface BuildTraversalViewModelArgs {
  graphInterface: GraphInterface;
  graphModel: GraphModelShape;
  startSet: ArdoqId[];
  traverseOptions: TraverseOptions;
}
export const buildTraversalViewModel = ({
  graphInterface,
  graphModel,
  startSet,
  traverseOptions,
}: BuildTraversalViewModelArgs): TraversalViewModel => {
  const { referenceTypes } = graphModel;
  const { traversedOutgoingReferenceTypes, traversedIncomingReferenceTypes } =
    referenceTypeOptionCache.getReferenceTypeOptions(
      graphInterface,
      graphModel,
      startSet,
      traverseOptions
    );
  return {
    referenceTypes,
    traversedOutgoingReferenceTypes,
    traversedIncomingReferenceTypes,
  };
};

interface GetTraversalConfigurationArgs {
  viewSettings: TraversalViewSettings;
  graphModel: GraphModelShape;
  scenarioRelatedComponentIds: ScenarioRelatedComponentIds;
}
export const getTraversalConfiguration = ({
  viewSettings,
  graphModel,
  scenarioRelatedComponentIds,
}: GetTraversalConfigurationArgs) => {
  const referenceTypeMap = buildReferenceTypeMap(graphModel);

  // Hide every scenario-related component (the full set populated from
  // setScenarioRelatedComponentIds), not just the user-driven subset on
  // `scenarioRelatedComponents`.
  const excludedComponentIds = scenarioRelatedComponentIds;

  const traverseOptions = getTraverseOptions({
    viewSettings,
    referenceTypeMap,
    excludedComponentIds,
  });
  return {
    traverseOptions,
    referenceTypeMap,
  };
};

interface GetGroupingRulesArgs {
  loadedScenarioData: LoadedScenarioData;
  showScopeRelated: boolean;
  scenarioRelatedComponentIds: ArdoqId[];
}

export const getScenarioGroup = ({
  loadedScenarioData,
  showScopeRelated,
  scenarioRelatedComponentIds,
}: GetGroupingRulesArgs): GroupingRule | null => {
  if (
    loadedScenarioData.scenarioData &&
    scenarioRelatedComponentIds.length > 0 &&
    showScopeRelated
  ) {
    const scenarioGroup: GroupingRule = {
      label: `Scenario: ${loadedScenarioData.scenarioName}`,
      type: GroupType.ALL,
      direction: ReferenceDirection.UNSET,
      targetId: '',
      workspaceId: '',
    };
    return scenarioGroup;
  }

  return null;
};
